<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useNotification } from '@kyvg/vue3-notification'
import { pikimoni } from '@/helpers/pikimoni'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import TeacherPikimoniInfoModal from '@/ui-kit/teacher/TeacherPikimoniInfoModal.vue'
import TeacherPikimoniSuccessModal from '@/ui-kit/teacher/TeacherPikimoniSuccessModal.vue'
import PikimoniExamplesModal, { type PikimoniExamplesModalExpose } from '../PikimoniExamplesModal.vue'

interface PageContext {
  has_pikimoni_binding: boolean
  has_full_plan: boolean | null
  has_trial_lesson_offer: boolean
  trial_lesson_offer_visible: boolean
  trial_lesson_offer_active: boolean
  can_disconnect_pikimoni: boolean
  new_requests_count: number
  urls: {
    trial_lesson_offer: string
    incoming_trial_lesson_requests: string
  }
}

const ctx = usePageContext<PageContext>()
const { notify } = useNotification()

const pikimoniInfo = useTemplateRef<{ open: () => void }>('pikimoniInfo')
const pikimoniExamplesModal = useTemplateRef<PikimoniExamplesModalExpose>('pikimoniExamplesModal')

const hasPikimoniBinding = ref(ctx.has_pikimoni_binding)
const hasFullPlan = ref(ctx.has_full_plan)
const hasTrialLessonOffer = ref(ctx.has_trial_lesson_offer)
const canDisconnectPikimoni = ref(ctx.can_disconnect_pikimoni)
const newRequestsCount = ref(ctx.new_requests_count)

const isInfoModalOpened = ref(false)
const isFullPlanPurchasedModalOpened = ref(false)

function connectPikimoni() {
  pikimoni.connect('teacher', 0, 'register').catch((err: unknown) => {
    console.error(err)
    const message =
      typeof err === 'object' && err && 'message' in err
        ? String(err.message)
        : 'Не удалось перейти ко входу в Pikimoni'
    notify({ type: 'error', title: message })
  })
}

function disconnectPikimoni() {
  pikimoni.disconnect('teacher').catch((err: unknown) => {
    console.error(err)
    const message =
      typeof err === 'object' && err && 'message' in err
        ? String(err.message)
        : 'Не удалось перейти ко входу в Pikimoni'
    notify({ type: 'error', title: message })
  })
}

async function loginToPikimoni() {
  const win = window.open('about:blank', '_blank')
  if (!win) {
    return
  }

  try {
    const url = await pikimoni.getLoginUrl('teacher', 0)
    win.location.href = url
  } catch (err: unknown) {
    console.error(err)
    win.close()

    let title = 'Не удалось перейти ко входу в Pikimoni'
    if (typeof err === 'string') title = err
    if (typeof err === 'object' && err && 'message' in err) title = String(err.message)
    notify({ type: 'error', title })
  }
}

onMounted(() => {
  const query = new URL(window.location.href).searchParams
  if (query.has('full_plan_purchased')) {
    isFullPlanPurchasedModalOpened.value = true
  }
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div class="content teacher-cabinet-page">
      <v-notifications position="bottom right" />
      <TeacherPikimoniSuccessModal :has-full-plan="hasFullPlan" />
      <TeacherPikimoniInfoModal ref="pikimoniInfo" />
      <PikimoniExamplesModal ref="pikimoniExamplesModal" />

      <v-modal v-model="isInfoModalOpened" title="Онлайн-преподавание">
        <div class="action-modal text-block pr-d-4">
          <p class="mb-2">
            Несмотря на то, что изначально методика <b>I Love English</b> планировалась как обучение в офлайн классе,
            она прекрасно подходит и для работы онлайн. Многие учителя не только успешно преподают английский онлайн по
            нашей методике, но также и разработали множество материалов для преподавания онлайн. Особенного внимания
            заслуживает платформа <b>Пикимони</b>, на которой расположена вся необходимая наглядность для преподавания
            по всем ступеням методики <b>I Love English</b>.
          </p>
          <p class="mb-2">Что нужно сделать, чтобы начать преподавать онлайн?</p>
          <ol>
            <li>
              Выбрать программу для видео конференций (Zoom, Яндекс Телемост, Google Meet и т.д.), в которой вам будет
              удобно работать.
            </li>
            <li>
              Если вы планируете работать на Пикимони, вам следует создать учетную запись на платформе Пикимони и
              ознакомиться с азами работы на платформе.
              <v-link mode="primary" class="mt-1" @click="connectPikimoni">
                Подключиться к Пикимони
                <v-icon class="ml-half" size="16">mdi-open-in-new</v-icon>
              </v-link>
            </li>
            <li>
              Заполнить анкету, чтобы родители могли вас найти и записаться на пробный урок, и начните принимать заявки.
              <v-link mode="primary" target="_blank" :href="ctx.urls.trial_lesson_offer" class="mt-1">
                Заполнить анкету
                <v-icon class="ml-half" size="16">mdi-open-in-new</v-icon>
              </v-link>
            </li>
            <li>
              Вы готовы! Вы можете проверять заявки от родителей.
              <v-link mode="primary" target="_blank" :href="ctx.urls.incoming_trial_lesson_requests" class="mt-1">
                Посмотреть заявки
                <v-icon class="ml-half" size="16">mdi-open-in-new</v-icon>
              </v-link>
            </li>
            <li>
              Если вы принимаете заявку, то вам необходимо связаться с родителем и согласовать дату и время пробного
              урока. Не забудьте рассказать родителю все, что ему нужно подготовить для занятия (скачать программу для
              видео конференции, подключиться к Пикимони, подготовить камеру и микрофон и т.д.). Вы также можете
              отклонить заявку, если по каким-то причинам вас что-то не устраивает.
            </li>
            <li>
              После пробного урока, если ребенок хочет продолжать заниматься, вы договариваетесь о постоянном времени
              занятий и условиях оплаты.
            </li>
            <li>
              После согласования постоянного времени занятий, обязательно расскажите родителю, на каком курсе будет
              обучаться его ребенок, а также, какие пособия ему нужно будет приобрести. Приобрести пособия возможно
              прямо из кабинета родителя.
            </li>
          </ol>
          <p class="pb-2"><b>Приятной работы онлайн!</b></p>
        </div>
      </v-modal>

      <v-modal v-model="isFullPlanPurchasedModalOpened" title="Полный доступ куплен!">
        <div class="action-modal text-block pr-d-4">
          <p class="mb-2">Вы оплатили полный доступ к платформе!</p>
          <p class="mb-2">
            Приступить к использованию платформы можно с изучения материалов САМОСТОЯТЕЛЬНО по данной ссылке
            <v-link mode="primary" href="https://shop.i-love-english.store/l/start_pikimoni">
              https://shop.i-love-english.store/l/start_pikimoni
            </v-link>
            Просмотр ВЕБИНАРОВ ОБЯЗАТЕЛЕН, в особенности по ступени I CAN SING, потому что именно там идет речь об
            устройстве платформы в целом! Обратите внимание, если просматривать вебинары с сайта YouTube (для этого
            кликните по названию вебинары), в описании к видео есть тайм-коды, по которым легко найти нужную информацию!
          </p>
          <p class="mb-2">
            Если же Вы захотите принять участие в живом вебинаре по первым трем ступеням (SING, SPEAK, READ), можно
            записаться на ближайшую дату на Вашей странице ПИКИМОНИ в разделе My webinar!
          </p>
          <p class="mb-2">Кроме того, приглашаем Вас в чаты:</p>
          <p class="mb-2">
            ✅ по вопросам платформы
            <v-link mode="primary" href="https://t.me/joinchat/R-IwQxujab4iHFjbaFl1Og"
              >https://t.me/joinchat/R-IwQxujab4iHFjbaFl1Og</v-link
            >
            Там коллеги помогают отвечать на вопросы, которые могут возникнуть! НО ПРЕЖДЕ старайтесь САМИ разобраться по
            материалам из рассылки!<br />
            ✅ по работе на платформе в больших группах (от 4 до 8 студентов). Рекомендуется разбивать студентов на
            мини-группы до 4 человек! Потому что эффективность занятий в больших группах падает ❗НО если такой
            возможности нет, для вас есть еще один чат ПИКИМОНИ+Zoom! Там разбираем работу на платформе с помощью
            демонстрации экрана и др. инструментов Zoom! Вступайте, если актуально!
            <v-link mode="primary" href="https://t.me/joinchat/R-IwQxm0oRl8MyWtEQIEpQ"
              >https://t.me/joinchat/R-IwQxm0oRl8MyWtEQIEpQ</v-link
            >
          </p>
          <p class="mb-2">
            Вступайте в нашу группу
            <v-link mode="primary" href="https://vk.com/pikimoni">https://vk.com/pikimoni</v-link>. Там Вы сможете
            узнавать о всех новинках на платформе! Есть различные темы в обсуждениях, где можно задавать вопросы по
            темам! Мы будем рады помочь!
          </p>
          <p class="pb-2">
            Желаем Вам удачного старта в ОНЛАЙН преподавании. Пусть платформа ПИКИМОНИ помогает Вам в этом!
          </p>
        </div>
      </v-modal>

      <header class="header">
        <h1 class="header__title">Онлайн-преподавание</h1>
      </header>

      <p>Этот раздел помогает учителям, преподающим онлайн, <b>найти учеников</b>.</p>
      <v-link class="text-title-bold my-1" mode="primary" @click="isInfoModalOpened = true">Как это работает?</v-link>

      <div class="tiles teacher-cabinet__tiles mt-4 mt-d-3">
        <div class="tile _pikimoni tiles__tile">
          <div class="tile__image mb-4">
            <v-icon width="160" height="190">online-teaching-pikimoni</v-icon>
            <v-icon class="tile__icon _pikimoni" width="132" height="40">pikimoni</v-icon>
          </div>

          <template v-if="hasPikimoniBinding">
            <template v-if="hasFullPlan">
              <h3 class="tile__title text-h3 mb-2">Пикимони</h3>
              <p class="tile__description mb-2">Входите в Пикимони из вашего аккаунта MirGovorit.</p>
              <v-button mode="primary" class="tile__button" @click="loginToPikimoni">Войти в Пикимони</v-button>
              <v-button
                v-if="canDisconnectPikimoni"
                mode="primary"
                class="tile__button mt-3"
                @click="disconnectPikimoni"
              >
                Отключиться от Пикимони (тест)
              </v-button>
            </template>

            <template v-else>
              <h3 class="tile__title text-h3 mb-2">Пикимони-Demo</h3>
              <p class="tile__description mb-2">
                Попробуйте Полный Доступ БЕСПЛАТНО. Зайдите в свой аккаунт Пикимони и активируйте его с помощью кнопки
                «Upgrade to Full Plan for Free!». После этого кликните на свою фамилию в правом верхнем углу и в разделе
                My Levels откройте нужный уровень на 30 дней бесплатного пользования. После бесплатного месячного
                использования платформы вы можете оплачивать нужную ступень по удобному тарифу. О ценах можно узнать в
                разделе «Цены» на сайте
                <v-link mode="primary" href="https://pikimoni.ru" target="_blank">pikimoni.ru</v-link>.
              </p>
              <v-button mode="primary" class="tile__button" @click="loginToPikimoni">Войти в Пикимони</v-button>
              <v-button
                v-if="canDisconnectPikimoni"
                mode="primary"
                class="tile__button mt-3"
                @click="disconnectPikimoni"
              >
                Отключиться от Пикимони (тест)
              </v-button>
            </template>
          </template>

          <template v-else>
            <h3 class="tile__title text-h3 mb-2">Присоединитесь к Пикимони</h3>
            <p class="tile__description mb-2">
              Попробуйте Полный Доступ на платформе Пикимони. Зарегистрируйтесь на Пикимони и зайдите в свой аккаунт
              Пикимони. Кликните на свою фамилию в Правом верхнем углу и в разделе My Levels откройте нужный уровень на
              30 дней бесплатного пользования. После бесплатного месячного использования платформы Вы можете оплачивать
              нужную ступень по удобному тарифу. О ценах можно узнать в разделе Цены на сайте
              <v-link mode="primary" href="https://pikimoni.ru" target="_blank">pikimoni.ru</v-link>.
            </p>
            <v-button mode="primary" class="tile__button" @click="connectPikimoni">Подключить Пикимони</v-button>
            <v-button v-if="canDisconnectPikimoni" mode="primary" class="tile__button mt-3" @click="disconnectPikimoni">
              Отключиться от Пикимони (тест)
            </v-button>
          </template>

          <v-link class="mt-3 mt-d-2" mode="primary" type="button" @click="pikimoniInfo?.open()"
            >Что такое Пикимони?</v-link
          >
          <v-link class="mt-3 mt-d-2" mode="primary" href="https://vk.com/pikimoni">Pikimoni Вконтакте</v-link>
          <v-link class="mt-3 mt-d-2" mode="primary" type="button" @click="pikimoniExamplesModal?.$open()"
            >Примеры уроков</v-link
          >
        </div>

        <div class="tile _form tiles__tile">
          <div class="tile__image mb-4">
            <v-icon width="160" height="190">online-teaching-form</v-icon>
          </div>
          <template v-if="hasTrialLessonOffer">
            <h3 class="tile__title text-h3 mb-2">Анкета</h3>
            <p class="tile__description mb-2">Не забывайте поддерживать анкету в актуальном состоянии.</p>
            <v-button mode="primary" class="tile__button" :href="ctx.urls.trial_lesson_offer"
              >Редактировать анкету</v-button
            >
          </template>
          <template v-else>
            <h3 class="tile__title text-h3 mb-2">Заполните анкету</h3>
            <p class="tile__description mb-2">
              Расскажите о себе, чтобы ученики могли найти вас, и записаться на пробный урок.
            </p>
            <v-button mode="primary" class="tile__button" :href="ctx.urls.trial_lesson_offer"
              >Заполнить анкету</v-button
            >
          </template>
        </div>

        <div class="tile _requests tiles__tile">
          <div class="tile__image mb-4">
            <v-icon width="160" height="190">online-teaching-requests</v-icon>
          </div>
          <h3 class="tile__title text-h3 mb-2">Отвечайте на заявки</h3>
          <template v-if="hasTrialLessonOffer">
            <p class="tile__description mb-2">Принимайте или отклоняйте заявки на пробный урок.</p>
            <div class="tile__requests-button d-flex align-center">
              <v-button mode="primary" class="tile__button" :href="ctx.urls.incoming_trial_lesson_requests"
                >Посмотреть заявки</v-button
              >
              <span v-if="newRequestsCount" class="tile__new-requests ml-1">{{ newRequestsCount }}</span>
            </div>
          </template>
          <template v-else>
            <p class="tile__description mb-2">Ученики будут находить вашу анкету и присылать заявки на пробный урок.</p>
            <div class="tile__requests-button d-flex align-center">
              <v-button mode="primary" class="tile__button" :href="ctx.urls.incoming_trial_lesson_requests"
                >Посмотреть заявки</v-button
              >
              <span v-if="newRequestsCount" class="tile__new-requests ml-1">{{ newRequestsCount }}</span>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/online_teaching/teacher_cabinet.scss' as *;
</style>
